import { ManifestContext } from 'global/cnr/contexts/ManifestContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import usePageNavigation from 'global/cnr/contexts/useHelpers/usePageNavigation';
import { useRenders } from 'global/cnr/contexts/useHelpers/useRenders';
import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Icon, Image, Sidebar } from 'semantic-ui-react';
import { _useAppSlimMode } from '../../App';
import ActionProvider from '../cnr/contexts/ActionContext';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import HelpProvider from '../cnr/contexts/HelpContext';
import { PageContext } from '../cnr/contexts/PageContext';
import { ParentContext } from '../cnr/contexts/ParentContext';
import { frameworkLeftTypes, frameworkRightTypes } from '../cnr/reducers/FrameworkReducer';
import { g_cns } from '../common/cns';
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import DataSidebar from '../components/swipers/DataSidebar';
import AppDashboard from '../components/viewers/AppDashboard';
import { mobileDeviceTypes } from '../devices';
import { allPageTypes, fullPageTypes } from '../enums/allPageTypes';
import { gEnums } from '../enums/globalEnums';
import { PageItemOptionsMenu } from '../sidebars/PageItemOptionsMenu';
import WrapperVertCols from '../wrappers/WrapperVertCols';
import AppContent from './AppContent';
import PageFrameworkLeft from './PageFrameworkLeft';
import PageFrameworkRight from './PageFrameworkRight';
import SettingsAppLeftMenu from './SettingsAppLeftMenu';
import SettingsAppRightMenu from './SettingsAppRightMenu';
import usePageFunctions from 'global/cnr/contexts/useHelpers/usePageFunctions';

const AppSettingsSidebarWithProvider = React.lazy(() => import('../viewSettings/AppSettingsSidebar'));
const UIDisplayEditor = React.lazy(() => import('../pageItem/modification/UIDisplayEditor'));

const useItemWizard = true

/** Controls the settings for the app
 * @param appContent  
 * @description Creates a three column grid. 
 * The left column contains the global settings. 
 * The right column contains the view/Wi settings. 
 * The middle column contains the app/mobileContent, which is passed in via props.
 */
const PageFramework = () => {

  const { handleNav_item, handleNav_path } = usePageNavigation()

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, fns, settings } = parentContext ?? {}
  const { currentSettings_state, appSettings_state, paps_state, storage_state, clientInfo_state, eventInfo_state, demo_state, regData_state } = states
  const { appSettings_handlers, analytics_handlers, regData_handlers } = handlers ?? {}
  const { appUser_fns } = fns
  const { homeSettings } = settings ?? {}
  const { global: global_home } = homeSettings ?? {}
  const { settingsOptions: settingsOptions_home } = global_home ?? {}
  const { handleShow_appDashboard: handleShow_appDashboard_as } = appSettings_handlers ?? {}
  const { handleLogging_path } = analytics_handlers ?? {}
  const { appUrls: homeAppUrls } = global_home ?? {}
  const { appUrls: appUrlz } = homeAppUrls ?? {}
  const { appStorageUrlFiles } = storage_state ?? {}
  const { isRegDemo } = regData_state ?? {}
  const { demoOn } = demo_state ?? {}
  const { clientData } = clientInfo_state ?? {}
  const { name: name_client } = clientData ?? {}
  const { eventData } = eventInfo_state ?? {}
  const { name: name_event } = eventData ?? {}

  const name_page = name_event || name_client || ''

  useRenders('PageFramework', true);
  const page_functions = usePageFunctions()
  const { getLink } = page_functions

  const fromReg = isRegDemo ? true : false
  const fromDemo = demoOn ? true : false

  const manifestContext = useContext(ManifestContext)
  const { manifest_state } = manifestContext ?? {}
  const { manifestIcons } = manifest_state ?? {}
  const { manifest_7 } = manifestIcons ?? {}
  const { fileUrl } = manifest_7 ?? {}

  // pageContext
  const pageContext = useContext(PageContext)
  const { homeSettings_global } = pageContext ?? {}
  const { settingsOptions } = homeSettings_global ?? {}
  const { useGutterText, gutterText } = settingsOptions ?? {}

  // papsContext 
  const { lastRootPath, pathViews, view, pathName } = paps_state ?? {}

  const appUserAccess = useAppUserAccess()

  const _viewPermissionType_settings = appUser_fns.validate_settingsAuth(appUserAccess, true)
  const _viewPermissionType_data = appUser_fns.validate_dataAuth(null, true)

  const allowSideSettings = _viewPermissionType_settings === gEnums.viewPermissionTypes.allow
  const allowSideData = _viewPermissionType_data === gEnums.viewPermissionTypes.allow

  // appSettingsContext 
  const { currentAppDevice, showAppDashboard: showAppDashboard_as, appSettingsOn, globalSettingsOn, settingsLeftOn, settingsRightOn, settingsPreviewOn, tempDataSource, toggleDeviceOn } = appSettings_state ?? {}

  const { msettings } = currentAppDevice ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { iconClickProps, appDimensions, showAppDashboard, selectedViewKey, frameworkRightType, frameworkLeftType, appViewMode, desktopMode, mediaDeviceType, modalContent, fullMode, isMobileDevice } = framework_state ?? {}
  const { handleShow_fullPage, handleShow_frameworkRight, handleShow_frameworkLeft, handleShow_appDashboard, handleShow_appBottomSidebar } = framework_handlers ?? {}

  let { width, height } = appDimensions ?? {}

  const _showAppDashboard = showAppDashboard || showAppDashboard_as

  const handleCancel = () => {
    if (handleShow_appDashboard) {
      handleShow_appDashboard()
    } else if (handleShow_appDashboard_as) {
      handleShow_appDashboard_as()
    }
  }

  const handleKillDemo = () => {
    regData_handlers.handleClear_demoMode()
    handleNav_item({ key: 'landing' })
  }

  const { aps_global, aps_viewItems, aps_views, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});

  // pageContext  
  const { clientOptions, eventOptions, appDataSource, appSurvey, display: display_global, projectOptions } = aps_global ?? {}
  const { appDataSourceType } = appDataSource ?? {}
  const { useDarkMode } = settingsOptions_home ?? {}
  const { fontFamily, useFontFamily } = display_global ?? {}
  const { surveyUrl } = appSurvey ?? {}
  const { useEventGutterText, eventGutterText, eventGutterColorStart, eventGutterColorEnd } = projectOptions ?? {}
  const _useGutterText = useEventGutterText || useGutterText
  const _appOptions = { ...clientOptions, ...eventOptions }
  let { weatherZip, weatherCountryCode, weatherCode } = _appOptions ?? {}

  const { themeColors } = aps_global ?? {}
  const { color1 } = themeColors ?? {}

  const gutterColorStart = eventGutterColorStart?.apply && eventGutterColorStart?.color ? eventGutterColorStart?.color : color1
  const gutterColorEnd = eventGutterColorEnd?.apply && eventGutterColorEnd?.color ? eventGutterColorEnd?.color : color1

  let _gutterText = eventGutterText || gutterText

  if (fromReg) {
    _gutterText += ' (REG DEMO}'
  } else if (fromDemo) {
    _gutterText += ' (DEMO}'
  }

  const hexToRGBA = (hex, opacity) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const gutterStyle = useEventGutterText ? {
    background: `linear-gradient(to bottom, ${gutterColorStart}, ${hexToRGBA(gutterColorEnd, 0)})`
  } : {};

  const _dataFromGs = appDataSourceType === gEnums.dataSourceTypes.googleSheet
  const _dataFromGsTemp = tempDataSource === gEnums.dataSourceTypes.googleSheet

  // const [showFullPage, setShowFullPage] = useState()
  const [frtLeftOn, setFrtLeftOn] = useState()
  const [frtRightOn, setFrtRightOn] = useState()

  const pageNavHome = {
    page: null,
    path: null,
    icon: null,
  }

  const getPagePath = (name) => {
    const pagePath = useMemo(() => {
      let path = '';
      let done = false;
      const spl = lastRootPath.split('/');

      spl.forEach(sp => {
        if (sp) {
          if (sp === name) {
            path += sp + '/';
            path += pathViews[name];
            done = true;
          } else if (!done) {
            path += sp + '/';
          }
        }
      });

      if (pathViews) {
        Object.keys(pathViews).forEach(pv => {
          if (pv !== view) {
            if (aps_viewItems && aps_viewItems[pv]) {
              const { pageNavigation } = aps_viewItems[pv];
              const { showPageHome, pageHomeIcon } = pageNavigation ?? {};
              if (showPageHome) {
                pageNavHome.path = getPagePath(pv);
                pageNavHome.page = pv;
                pageNavHome.icon = pageHomeIcon;
              }
            }
          }
        });
      }

      return path;
    }, [lastRootPath, pathViews, aps_viewItems, view]); // Memoize based on dependencies

    return pagePath;
  };

  useEffect(() => {
    // this will have a value IF a bottom navigation item is clicked
    if (iconClickProps) {
      const { clickedItem, itemKey } = iconClickProps ?? {}
      const { key, itemClick } = clickedItem ?? {}
      const { clickToType, pdf: pdf_name } = itemClick ?? {}

      switch (clickToType) {
        case gEnums.clickToTypes.pdf:
          const { pdf } = appStorageUrlFiles ?? {}
          if (pdf && pdf[pdf_name]) {
            const _currentPdf = pdf[pdf_name]
            if (_currentPdf && _currentPdf.urls && _currentPdf.urls.full) {
              window.open(_currentPdf.urls.full, '_blank')
            }
          }
          break;

        default:

          if (fullPageTypes[key]) {
            if (useItemWizard) {
              handleShow_fullPage(key)
            } else {
              handleNav_item(clickedItem, itemKey)
            }
          } else {
            switch (key) {
              case 'slideout':
                framework_handlers.handleShow_appSidebar()
                break;

              case allPageTypes.navOptions:
                handleShow_appBottomSidebar(fpw_navOptions())
                // setShowPageItemOptions(true)
                break;

              case allPageTypes.media:
                handleNav_item(clickedItem)
                break;

              case allPageTypes.survey:
                window.open(surveyUrl, '_blank')
                break;

              case allPageTypes.weather:
                const weatherLink = getLink(appUrlz, 'weather', isMobileDevice)

                if (weatherLink) {
                  let url = weatherLink
                  url = weatherCode ? url.replace('ZIPCODE', weatherCode) : url.replace('ZIPCODE', weatherZip)
                  if (weatherCode) {
                    url = url.replace(':4:US', '')
                  } else {
                    if (weatherCountryCode) { url = url.replace('US', weatherCountryCode) }
                  }
                  window.open(url, '_blank')
                }
                break;
              default:
                if (clickedItem) {
                  if (clickedItem.key === pageNavHome.page) {
                    handleNav_path(pageNavHome.path)
                  } else {
                    handleNav_item(clickedItem)
                  }
                }
            }
          }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconClickProps]);

  useEffect(() => {
    switch (frameworkLeftType) {
      case frameworkLeftTypes.appProfilesManager:
      case frameworkLeftTypes.appData:
      case frameworkLeftTypes.appUsersManager:
      case frameworkLeftTypes.appUsersPresense:
      case frameworkLeftTypes.bulkImageUpload:
      case frameworkLeftTypes.createNewEvent:
      case frameworkLeftTypes.recreateNewEvent:
      case frameworkLeftTypes.settings:
      case allPageTypes.credentialScan:
      case allPageTypes.meetingsManager:
      case allPageTypes.notificationsManager:
      case allPageTypes.qAndAManager:
      case allPageTypes.surveyManager:
      case allPageTypes.ticketingManager:
      case allPageTypes.votingManager:
        setFrtLeftOn(true)
        break;
      default:
        setFrtLeftOn(false)
      // nothing 
    }

    switch (frameworkRightType) {
      case frameworkRightTypes.appUserProfile:
      case frameworkRightTypes.sportScoresViewer:
      case frameworkRightTypes.dataConstraints:
      case frameworkRightTypes.dataModification:
      case frameworkRightTypes.display:
      case frameworkRightTypes.filters:
      case frameworkRightTypes.google:
      case frameworkRightTypes.pageDataAdd:
      case frameworkRightTypes.pageDataEdit:
      case frameworkRightTypes.pageLinkData:
      case frameworkRightTypes.pageMedia:
      case frameworkRightTypes.props:
      case frameworkRightTypes.showDataJson:
      case frameworkRightTypes.showLog:
      case frameworkRightTypes.sorting:
      case frameworkRightTypes.settings:
        setFrtRightOn(true)
        break;
      default:
        setFrtRightOn(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkLeftType, frameworkRightType]);

  const _appSettingsOn = appSettingsOn || settingsLeftOn || settingsRightOn || frameworkRightType && frameworkRightType === frameworkRightTypes.pageSettingsFull || globalSettingsOn ? true : false
  const desktopView = desktopMode ? true : false

  // local
  const authPermissionType = appUser_fns.validate_settingsAuth(appUserAccess)
  const allowSettings = authPermissionType === gEnums.viewPermissionTypes.allow

  const mdsx = []

  if (msettings && msettings.w) { width = msettings.w }
  if (msettings && msettings.h) { height = msettings.h }

  const mds = mobileDeviceTypes()

  let sideCn = 'app-side'

  if (frameworkLeftType) {
    sideCn += ' lft'
  }

  mds.forEach(item => {
    const z = { ...item }
    delete z.msettings
    mdsx.push(z)
  })

  // const segWrapperStyle = (desktopView) ? { height: '100%', width: '100%' } : { height: height + wrapGap, width: width + wrapGap, border: wrapBorder + 'px solid' }
  const segStyle = (desktopView) ? { height: '100%', width: '100%', border: 0, padding: 0 } : { height: height, width: width, border: 0, padding: 0 }
  if (useFontFamily && fontFamily) { segStyle.fontFamily = fontFamily }


  let showLeftSettings = false

  const settingsOnly = appViewMode === gEnums.appViewModes.settings

  switch (view) {
    case 'home':
    case 'clients':
    case 'events':
      showLeftSettings = true
      break;
    default:
      break;
  }


  const fpw_navOptions = () => <PageItemOptionsMenu />

  /**
   * 
   * @param {boolean} isGlobal 
   * @returns an 
   */
  const appSidebar_settings = (isGlobal) => <Suspense fallback={<SuspenseDimmer origin={'PageFramework'} />}>
    <AppSettingsSidebarWithProvider isGlobal={isGlobal} showLeftSettings={showLeftSettings} />
  </Suspense>


  const displayEditor = (contents) => {
    return <div className={'display-editor'}>
      {contents}
      <UIDisplayEditor segStyle={segStyle} />
    </div>
  }

  /**
   * 
   * @returns AppContent
   */
  const smartPhoneAppContent = () => {

    const _showEditor = false

    let cn = desktopView ? g_cns.settings_app_desktop_wrap : g_cns.settings_app_mobile_wrap

    if (settingsPreviewOn) {
      cn += ' settings-preview global'
    } else {
      cn += ' not-live'
    }

    if (fullMode) { cn += ' fullMode' }

    if (_showEditor) {
      return displayEditor(<div className={cn}  >
        <div id={'page-framework'} className='page-content-container'>
          <AppContent />
          {modalContent && modalContent}
        </div>
      </div>)
    } else {
      return <div className={cn}>
        <div id={'page-framework'} className='page-content-container'>
          <AppContent />
          {modalContent && modalContent}
        </div>
      </div>
    }
    // }
  }

  const BlankColumnDiv = (isGlobal) => {
    if (isGlobal) {
      const cn = sideCn + '-l'
      if (mediaDeviceType !== gEnums.mediaDeviceTypes.mobile) {
        return <div className={cn} ><SettingsAppLeftMenu /></div>
      }
    } else {
      const cn = sideCn + '-r'
      if (mediaDeviceType !== gEnums.mediaDeviceTypes.mobile && (allowSideSettings || allowSideData)) {
        return <div className={cn} ><SettingsAppRightMenu allowSideData={allowSideData} /></div>
      } else {
        return <div className={cn} ></div>
      }
    }
  }

  const sidebar_left = () => <DataSidebar
    direction={'left'}
    visible={frtLeftOn}
    content={frtLeftOn && <PageFrameworkLeft />}
    onHide={handleShow_frameworkLeft}
  />

  const sidebar_right = () => <DataSidebar
    direction={'right'}
    visible={frtRightOn}
    content={frtRightOn && <PageFrameworkRight showLeftSettings={showLeftSettings} />}
    onHide={handleShow_frameworkRight}
  />

  const div_settings = (isGlobal, cn) => <div className={sideCn} >
    {_appSettingsOn && appSidebar_settings(isGlobal)}
  </div>


  const div_side = (isGlobal) => {
    if (!allowSettings) { return BlankColumnDiv(isGlobal) }
    if (!isGlobal) {
      switch (view) {
        case allPageTypes.appProfile:
          return BlankColumnDiv(false)
        default:
        // nothing
      }
    }
    return div_settings(isGlobal)
  }

  /**
   * 
   * @returns smartPhoneAppContent > AppContent
   */
  const appContainer = () => {

    let cn_sa_containter = g_cns.settings_app_container
    let cn_sa_content = g_cns.settings_app_content

    if (desktopView) { cn_sa_content += ' dt-mode' }

    if (_dataFromGsTemp) { cn_sa_containter += ' gds temp' }
    if (_dataFromGs) { cn_sa_containter += ' gds' }
    if (_useAppSlimMode) { cn_sa_containter += ' app-me' }

    return <div
      className={cn_sa_containter}
      style={gutterStyle}>
      <div className={cn_sa_content} >
        {smartPhoneAppContent()}
      </div>
    </div>
  }

  const AppColumnDiv = (w) => <div className={'app-main'}>
    {appContainer()}
    {_useGutterText && <div className='app-mark'>
      {fileUrl && <div><Image src={fileUrl} size='tiny' /></div>}
      <div>{`${_gutterText} ${name_page ? ':' : ''} ${name_page}`}</div>
      {isRegDemo || demoOn && <Icon style={{ margin: '1em' }} name='delete' onClick={handleKillDemo}></Icon>
      }</div>}
  </div>

  const setGis = (left, right) => {
    return {
      left,
      right
    }
  }

  let cn_framework = g_cns.framwork_container

  if (useDarkMode) { cn_framework += ' settings-dark-mode' }
  if (settingsOnly) { cn_framework += ' so' }

  if (toggleDeviceOn) {
    switch (currentAppDevice?.key) {
      case gEnums.mediaDeviceTypes.smartphone:
        cn_framework += ' frwk-' + gEnums.mediaDeviceTypes.desktopMobile
        break;
      default:
        cn_framework += ' frwk-' + gEnums.mediaDeviceTypes.smartphone
    }
  } else {
    cn_framework += ' frwk-' + currentAppDevice?.key
  }

  const getGridItemSettings = () => {

    let _gridItems = {
      left: false,
      right: false
    }


    // fallback (mobile, not desktop view)
    if (appSettingsOn) {
      _gridItems = setGis(true, true)
    } else if (globalSettingsOn) {
      _gridItems = setGis(true, false)
    } else if (settingsRightOn) {
      _gridItems = setGis(false, true)
    } else if (settingsLeftOn) {
      _gridItems = setGis(true, false)
    } else if (frameworkRightType) {
      if (frameworkRightTypes[frameworkRightType]) {
        _gridItems = setGis(false, true)
      }
    }

    return _gridItems
  }

  const gridContent = useMemo(() => {

    if (settingsOnly) {
      return appSidebar_settings(true)
    }


    // IF this is a mobile device, just show the AppContent
    if (mediaDeviceType === gEnums.mediaDeviceTypes.mobile) {
      return <AppContent />
    }

    // get the setting for the grid
    let _gridItems = getGridItemSettings()

    // HERE are the main columns
    return <WrapperVertCols mvc={desktopMode ? 'app' : 'app'} noDarkMode={true} forApp={true}>
      {/* <div className='app-top-nav'>Top Nav</div> */}
      {_gridItems.left ? div_side(true) : BlankColumnDiv(true)}
      {AppColumnDiv()}
      {_gridItems.right ? div_side(false) : BlankColumnDiv(false)}
    </WrapperVertCols>


  }, [
    pathViews,
    // settingsOnly,
    // mediaDeviceType, 
    // desktopView,
    // appSettingsOn,
    // globalSettingsOn,
    pathViews,
    settingsLeftOn,
    settingsRightOn,
    frameworkRightType,
    currentAppDevice,
    // pageWidths,
    // useDarkMode,
    // _useGridDarkMode,
    // g_cns.app_settings_grid,
    _gutterText,
    _useGutterText,
    name_page
  ])

  const appViewerContent = () => {
    if (_showAppDashboard) {
      return <ActionProvider><AppDashboard handleCancel={handleCancel} selectedDataMode={selectedViewKey} /></ActionProvider>
    }
  }

  const sidebar_main = () => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {/* {sidebar_bottom()} */}
    {sidebar_left()}
    {sidebar_right()}
    <Sidebar.Pusher dimmed={frtRightOn} className={'sbr-push-main h100'}>
      {appViewerContent()}
      {gridContent}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const pfContent = () => <div className={cn_framework}>
    {/* {<MultiGroupDnd />} */}
    {sidebar_main()}
  </div>

  return <HelpProvider>
    {pfContent()}
  </HelpProvider>

}

export default PageFramework 